import {
  Badge,
  Checkbox,
  Group,
  Loader,
  NumberInput,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EntitySelect } from '../../components/EntityField';
import { useBaseCurrency, useIslandToday } from '../../hooks/useSeedLocation';
import { enumLabel } from '../../lib/enums';
import { formatDate, formatMoney, toIsoDate, toMajorUnits, toMinorUnits } from '../../lib/format';

/** One delivery a shipping charge covers, and the amount stated against it when split by hand. */
export interface CoveredDelivery {
  receiptId: string;
  /** null means "work this one out from the rest" — see the note under the list. */
  shareMinor: number | null;
}

interface Delivery {
  id: string;
  supplierId: string;
  supplierName: string | null;
  invoiceNumber?: string | null;
  receivedAt: string;
  status: string;
  lineCount: number;
  goodsTotalBaseMinor: number;
}

const RECEIPTS_PATH = '/admin/v1/suppliers/receipts';

/**
 * Windows a person actually asks for. A crossing is billed days after it sailed, not months, so
 * the useful default is recent — but the bill for a slow forwarder does turn up late, hence
 * "Everything".
 */
const PERIODS = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'Everything' },
];

/**
 * "Last 30 days" counted back from the bar's day, not from Greenwich.
 *
 * `new Date().toISOString().slice(0, 10)` is the pattern this repository has now banned by name —
 * it is Greenwich's calendar, and for the six evening hours when goods actually land on Roatán it
 * answers tomorrow. The cost here is only a window edge rather than a stored date, which is exactly
 * why it survived two sweeps: it is cheap enough to skip and identical enough to mislead the next
 * person who copies it.
 */
function since(period: string, today: string): string | undefined {
  if (period === 'all') return undefined;
  const d = new Date(`${today}T12:00:00`);
  d.setDate(d.getDate() - Number(period));
  return toIsoDate(d) ?? undefined;
}

export interface DeliveryPickerProps {
  value: CoveredDelivery[];
  onChange: (next: CoveredDelivery[]) => void;
  /** The carrier's currency: a stated share is part of THEIR bill, not of the supplier invoice. */
  currency: string;
  /** Ask for an amount against each delivery, because this charge is being split by hand. */
  statedShares: boolean;
  /** Delivery id → the other bill already covering it, so one crossing is not paid for twice. */
  alreadyCovered?: Map<string, string>;
  disabled?: boolean;
}

/**
 * Which deliveries a carrier's bill covered.
 *
 * The identifying question is never "which id is it" — it is "which crossing was this?", and that
 * is answered by what was ON it: who sent the goods, the day they landed, their invoice number and
 * what the goods came to. So every row says all four, and the picker filters by supplier and by
 * period the way somebody holding a waybill would narrow it down.
 *
 * Deliveries already chosen stay at the top of the list whatever the filters say. Otherwise
 * narrowing the period would hide a delivery that is still being charged for, and the only way to
 * discover it would be to save and reopen.
 */
export function DeliveryPicker(props: DeliveryPickerProps) {
  // The goods total on a delivery is already converted into the money the bar keeps its books in,
  // whatever the supplier billed. Printing it with a hard-coded dollar sign was a twenty-six-fold
  // misreading waiting to happen the day those books are kept in lempiras.
  const base = useBaseCurrency();
  const islandToday = useIslandToday();
  const [period, setPeriod] = useState(props.value.length > 0 ? 'all' : '90');
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const from = since(period, islandToday);
  const query = useQuery({
    queryKey: listKey(RECEIPTS_PATH, { from, supplierId: supplierId ?? undefined }),
    queryFn: () => {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (supplierId) params.set('supplierId', supplierId);
      const qs = params.toString();
      return rawApi<Delivery[]>('GET', qs ? `${RECEIPTS_PATH}?${qs}` : RECEIPTS_PATH);
    },
  });

  // Every delivery this picker has ever loaded, so a chosen one keeps its name and total when the
  // filters move away from it. Without the cache, narrowing the period turns a chosen row into an
  // anonymous line the operator cannot recognise well enough to decide whether to keep it.
  const seen = useRef(new Map<string, Delivery>());
  for (const d of query.data ?? []) seen.current.set(d.id, d);

  const chosen = new Map(props.value.map((v) => [v.receiptId, v]));
  const term = search.trim().toLowerCase();
  const matches = (d: Delivery) =>
    !term ||
    (d.supplierName ?? '').toLowerCase().includes(term) ||
    (d.invoiceNumber ?? '').toLowerCase().includes(term);

  const rows: { id: string; delivery?: Delivery }[] = [
    ...props.value.map((v) => ({ id: v.receiptId, delivery: seen.current.get(v.receiptId) })),
    ...(query.data ?? [])
      .filter((d) => !chosen.has(d.id) && matches(d))
      .map((d) => ({ id: d.id, delivery: d })),
  ];

  const toggle = (id: string, on: boolean) => {
    props.onChange(
      on
        ? [...props.value, { receiptId: id, shareMinor: null }]
        : props.value.filter((v) => v.receiptId !== id),
    );
  };

  const setShare = (id: string, shareMinor: number | null) => {
    props.onChange(props.value.map((v) => (v.receiptId === id ? { ...v, shareMinor } : v)));
  };

  const chosenGoodsBaseMinor = props.value.reduce(
    (sum, v) => sum + (seen.current.get(v.receiptId)?.goodsTotalBaseMinor ?? 0),
    0,
  );

  return (
    <Stack gap="xs">
      <Group gap="sm" grow align="flex-end" wrap="wrap">
        <EntitySelect
          // Deliveries only ever come from a vendor that sells goods, so offering the carriers here
          // is offering filters that can only ever empty the list.
          entity="goodsSupplier"
          label="Only deliveries from"
          value={supplierId}
          onChange={setSupplierId}
          disabled={props.disabled}
        />
        <TextInput
          label="Find"
          placeholder="Supplier or invoice number"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          disabled={props.disabled}
        />
      </Group>
      <SegmentedControl
        size="xs"
        data={PERIODS}
        value={period}
        onChange={setPeriod}
        disabled={props.disabled}
      />

      {query.isError ? (
        <Text size="sm" c="red.7">
          The deliveries could not be loaded. Nothing has been lost — try again in a moment.
        </Text>
      ) : query.isLoading ? (
        <Group gap="xs" py="md" justify="center">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading deliveries…
          </Text>
        </Group>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed" py="sm">
          No deliveries in this period. Widen the dates, or record the delivery first and come back to this
          charge.
        </Text>
      ) : (
        <ScrollArea.Autosize mah={300} type="auto">
          <Stack gap={2} pr="sm">
            {rows.map(({ id, delivery }) => {
              const picked = chosen.get(id);
              const elsewhere = props.alreadyCovered?.get(id);
              return (
                <Group key={id} justify="space-between" wrap="nowrap" gap="sm" py={4}>
                  <Checkbox
                    checked={Boolean(picked)}
                    onChange={(e) => toggle(id, e.currentTarget.checked)}
                    disabled={props.disabled}
                    styles={{ labelWrapper: { minWidth: 0 } }}
                    label={
                      delivery ? (
                        <div style={{ minWidth: 0 }}>
                          <Group gap="xs" wrap="nowrap">
                            <Text size="sm" fw={600}>
                              {delivery.supplierName ?? 'Unnamed supplier'}
                            </Text>
                            {delivery.invoiceNumber && (
                              <Text size="sm" c="dimmed">
                                {delivery.invoiceNumber}
                              </Text>
                            )}
                            {delivery.status === 'draft' && (
                              <Badge size="xs" variant="light" color="gray">
                                Not confirmed yet
                              </Badge>
                            )}
                          </Group>
                          <Text size="xs" c="dimmed">
                            {formatDate(delivery.receivedAt)} · {delivery.lineCount}{' '}
                            {delivery.lineCount === 1 ? 'item' : 'items'} ·{' '}
                            {formatMoney(delivery.goodsTotalBaseMinor, base)} of goods
                            {elsewhere ? ` · already on ${elsewhere}` : ''}
                          </Text>
                        </div>
                      ) : (
                        <Text size="sm" c="dimmed">
                          A delivery outside these dates — untick to stop charging it
                        </Text>
                      )
                    }
                  />
                  {props.statedShares && picked && (
                    <NumberInput
                      size="xs"
                      w={150}
                      aria-label="Amount of the bill for this delivery"
                      placeholder="Share out the rest"
                      value={toMajorUnits(picked.shareMinor)}
                      onChange={(v) => setShare(id, v === '' ? null : (toMinorUnits(v) ?? null))}
                      decimalScale={2}
                      thousandSeparator=","
                      hideControls
                      disabled={props.disabled}
                    />
                  )}
                </Group>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>
      )}

      <Text size="xs" c="dimmed">
        {props.value.length === 0
          ? 'Nothing chosen yet.'
          : `${props.value.length} ${props.value.length === 1 ? 'delivery' : 'deliveries'} chosen · ${formatMoney(chosenGoodsBaseMinor, base)} of goods between them.`}{' '}
        Goods are shown in one money throughout, so deliveries billed in lempiras and in dollars can be
        compared.
        {props.statedShares
          ? ` The amounts you write in are in ${enumLabel('currency', props.currency)}, the way the carrier billed them — leave one blank and that delivery takes a share of whatever is left over, by value.`
          : ''}
      </Text>
    </Stack>
  );
}
